<template>
  <div class="card border-0 bg-transparent">
    <div class="card-body p-0">
      <div class="d-flex align-items-end mb-4">
        <einzelperson_icon class="me-3"></einzelperson_icon>
        <h5 class="font-xl-links-bold mb-0">{{ personNummer }}. Person</h5>
      </div>

      <div class="row g-3">
        <!-- Row 1: Anrede + Titel -->
        <div class="col-md-6">
          <SelectField
            :id="`vpAnrede${item.id}`"
            label="Anrede *"
            v-model="localAnrede"
            :options="anredeOptions"
            placeholder="Bitte auswählen"
          />
        </div>

        <div class="col-md-6">
          <SelectField
            :id="`vpTitel${item.id}`"
            label="Akademischer Titel"
            v-model="localTitel"
            :options="titelOptions"
            placeholder="Bitte auswählen"
          />
        </div>

        <!-- Row 2: Vorname + Nachname -->
        <div class="col-md-6">
          <TextField
            :id="`vpVorname${item.id}`"
            label="Vorname *"
            v-model="localVorname"
          />
        </div>

        <div class="col-md-6">
          <TextField
            :id="`vpNachname${item.id}`"
            label="Nachname *"
            v-model="localNachname"
          />
        </div>

        <!-- Row 3: AOK-Versicherten-Nr. (nur vigoselect) + VN-Auswahl -->
        <div class="col-md-6">
          <TextField
            v-if="isVigoselect"
            :id="`vpAoknummer${item.id}`"
            label="AOK Versicherten-Nr. *"
            v-model="localAoknummer"
          />
        </div>

        <div class="col-md-6">
          <SelectField
            :id="`vpIstVN${item.id}`"
            label="Ist diese Person der Antragsteller/Versicherungsnehmer? *"
            v-model="localIstVN"
            :options="istVNOptions"
            placeholder="Ihre Auswahl"
            @update:modelValue="onVnSelectionChanged"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import moment from 'moment';
import SelectField from '@/fields/SelectField.vue';
import TextField from '@/fields/TextField.vue';
import einzelperson_icon from '@/components/icons/einzelperson_icon.vue';
import { useMainStore } from '@/stores/useMainStore.js';

const mainStore = useMainStore();

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  index: {
    type: Number,
    required: true
  },
  istVN: {
    type: Boolean,
    default: false
  }
});

const isVigoselect = computed(() => mainStore.kanal === 'vigoselect');

const emit = defineEmits(['update:item', 'vn-changed']);

// Person-Nummer (1-basiert)
const personNummer = computed(() => props.index + 1);

// Optionen für Select-Felder
const anredeOptions = [
  { value: 'Herr', text: 'Herr' },
  { value: 'Frau', text: 'Frau' }
];

const titelOptions = [
  { value: '', text: '--' },
  { value: 'Dr.', text: 'Dr.' },
  { value: 'Prof.', text: 'Prof.' },
  { value: 'Prof. Dr.', text: 'Prof. Dr.' }
];

// Prüft, ob die Person mindestens 18 Jahre alt ist (taggenau)
const istVolljaehrig = computed(() => {
  if (!props.item.geburtstag) return false;
  
  const geburtstag = moment(props.item.geburtstag, 'YYYY-MM-DD');
  const heute = moment();
  
  // Berechne das Alter in Jahren
  const alter = heute.diff(geburtstag, 'years');
  
  return alter >= 18;
});

// VN-Optionen: nur "Ja" anzeigen, wenn Person volljährig ist
const istVNOptions = computed(() => {
  if (istVolljaehrig.value) {
    return [
      { value: 'ja', text: 'Ja' },
      { value: 'nein', text: 'Nein' }
    ];
  } else {
    return [
      { value: 'nein', text: 'Nein' }
    ];
  }
});

// Lokale reaktive Werte mit zwei-Wege-Bindung
const localAnrede = computed({
  get: () => props.item.anrede || '',
  set: (value) => emitUpdate('anrede', value)
});

const localTitel = computed({
  get: () => props.item.titel || '',
  set: (value) => emitUpdate('titel', value)
});

const localVorname = computed({
  get: () => props.item.vorname || '',
  set: (value) => emitUpdate('vorname', value)
});

const localNachname = computed({
  get: () => props.item.nachname || '',
  set: (value) => emitUpdate('nachname', value)
});

const localAoknummer = computed({
  get: () => props.item.aoknummer || '',
  set: (value) => emitUpdate('aoknummer', value)
});

// VN-Auswahl lokal halten, damit "Nein" sichtbar bleibt
const localIstVN = ref(props.istVN ? 'ja' : 'nein');

watch(
  () => props.istVN,
  (value) => {
    localIstVN.value = value ? 'ja' : 'nein';
  }
);

// Wenn Person nicht mehr volljährig ist und aktuell als VN ausgewählt ist, zurücksetzen
watch(
  () => istVolljaehrig.value,
  (volljaehrig) => {
    if (!volljaehrig && localIstVN.value === 'ja') {
      localIstVN.value = 'nein';
      emit('vn-changed', {
        itemId: props.item.id,
        isVN: false
      });
    }
  }
);

// Emittiert Update für einzelnes Feld
function emitUpdate(field, value) {
  const updatedItem = { ...props.item, [field]: value };
  emit('update:item', updatedItem);
}

// VN-Auswahl geändert
function onVnSelectionChanged(value) {
  localIstVN.value = value;
  emit('vn-changed', {
    itemId: props.item.id,
    isVN: value === 'ja'
  });
}
</script>

<style scoped>
.font-xl-links-bold {
  font-weight: 700;
}
</style>
